import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RequireAuth, RequireSubscription } from "@/components/RequireAuth";
import { RequireAdmin } from "@/components/RequireAdmin";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { Home } from "@/pages/Home";
import { About } from "@/pages/About";
import { Pricing } from "@/pages/Pricing";
import { Contact } from "@/pages/Contact";
import { Terms } from "@/pages/Terms";
import { Privacy } from "@/pages/Privacy";
import { Zeroconf } from "@/pages/Zeroconf";
import { Ipam } from "@/pages/Ipam";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Dashboard } from "@/pages/Dashboard";
import { Clients } from "@/pages/Clients";
import { ClientDetail } from "@/pages/ClientDetail";
import { MockCheckout } from "@/pages/MockCheckout";
import { MockPortal } from "@/pages/MockPortal";
import { Portal } from "@/pages/Portal";
import { PortalDemo } from "@/pages/PortalDemo";
import { Tools } from "@/pages/Tools";
import { SubnetCalculator } from "@/pages/SubnetCalculator";
import { DnsHealth } from "@/pages/DnsHealth";
import { CveLookup } from "@/pages/CveLookup";
import { TlsInspector } from "@/pages/TlsInspector";
import { SecurityHeaders } from "@/pages/SecurityHeaders";
import { PasswordBreach } from "@/pages/PasswordBreach";
import { Nis2 } from "@/pages/Nis2";
import Alerts from "@/pages/Alerts";
import AlertDetail from "@/pages/AlertDetail";
import SharedAnswer from "@/pages/SharedAnswer";
import PublicAuditTool from "@/pages/PublicAuditTool";
import PublicAuditResult from "@/pages/PublicAuditResult";
import { BetaRedeem } from "@/pages/BetaRedeem";
import { AdminBeta } from "@/pages/admin/AdminBeta";
import { AdminFeedback } from "@/pages/admin/AdminFeedback";
import { Toaster } from "@/components/ui/sonner";

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route element={<Layout />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/services" element={<Navigate to="/" replace />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/zeroconf" element={<Zeroconf />} />
                        <Route path="/ipam" element={<Ipam />} />
                        <Route path="/tools" element={<Tools />} />
                        <Route path="/tools/subnet-calculator" element={<SubnetCalculator />} />
                        <Route path="/tools/dns-health" element={<DnsHealth />} />
                        <Route path="/tools/tls-inspector" element={<TlsInspector />} />
                        <Route path="/tools/security-headers" element={<SecurityHeaders />} />
                        <Route path="/tools/password-breach" element={<PasswordBreach />} />
                        <Route path="/tools/nis2-assessment" element={<Nis2 />} />
                        <Route path="/tools/nis2" element={<Navigate to="/tools/nis2-assessment" replace />} />
                        <Route path="/tools/cve-lookup" element={<CveLookup />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route
                            path="/dashboard"
                            element={
                                <RequireAuth>
                                    <Dashboard />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/clients"
                            element={
                                <RequireAuth>
                                    <Clients />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/clients/:slug"
                            element={
                                <RequireAuth>
                                    <ClientDetail />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/alerts"
                            element={
                                <RequireAuth>
                                    <Alerts />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/alerts/:alertId"
                            element={
                                <RequireAuth>
                                    <AlertDetail />
                                </RequireAuth>
                            }
                        />
                        {/* Public share-an-answer page — Phase D.3. Indexable. */}
                        <Route path="/share/:shareId" element={<SharedAnswer />} />
                        {/* Phase E — Public Surface Audit (free 1/day, Pro unlimited) */}
                        <Route path="/tools/public-audit" element={<PublicAuditTool />} />
                        <Route path="/tools/public-audit/:scanId" element={<PublicAuditResult />} />
                        <Route
                            path="/billing/mock-checkout"
                            element={
                                <RequireAuth>
                                    <MockCheckout />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/billing/mock-portal"
                            element={
                                <RequireAuth>
                                    <MockPortal />
                                </RequireAuth>
                            }
                        />
                        <Route path="*" element={<Home />} />
                    </Route>
                    {/* Beta redeem — fully public, no Layout (cleaner sign-up flow) */}
                    <Route path="/beta/redeem/:token" element={<BetaRedeem />} />
                    {/* Admin */}
                    <Route
                        path="/admin/beta"
                        element={
                            <RequireAdmin>
                                <AdminBeta />
                            </RequireAdmin>
                        }
                    />
                    <Route
                        path="/admin/feedback"
                        element={
                            <RequireAdmin>
                                <AdminFeedback />
                            </RequireAdmin>
                        }
                    />
                    {/* /portal renders full-bleed (no marketing Layout wrapper) */}
                    <Route
                        path="/portal"
                        element={
                            <RequireSubscription>
                                <Portal />
                            </RequireSubscription>
                        }
                    />
                    {/* /portal/demo — fully public, read-only ZeroConf preview */}
                    <Route path="/portal/demo" element={<PortalDemo />} />
                </Routes>
                <FeedbackWidget />
            </BrowserRouter>
            <Toaster
                position="top-right"
                richColors
                closeButton
                theme="dark"
                toastOptions={{
                    classNames: {
                        toast: "!bg-surface !border !border-white/[0.08] !text-text-primary !shadow-heavy-popover",
                        title: "!font-medium",
                        description: "!text-text-muted",
                        success: "!border-accent-mint/40",
                        error: "!border-accent-red/40",
                    },
                }}
            />
        </div>
    );
}

export default App;
